import { useState, useEffect } from "react";
import { Box } from "@mui/material";
import { useNavigate, useParams } from "react-router";
import { useFormik } from "formik";
import * as Yup from "yup";
import { request } from "../../config/request.ts";
import { toast } from "react-toastify";
import PageHeader from "../../shared/PageHeader/index.tsx";
import TextField from "../../shared/TextField/index.tsx";
import SelectField from "../../shared/SelectField/index.tsx";
import PageWrapper from "../../shared/PageWrapper/index.tsx";
import "../../styles/scss/Form.scss";

export default function BankAccountCreateEdit() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(false);

  const formik = useFormik({
    initialValues: {
      accountName: "",
      holderName: "",
      accountNo: "",
      openingBalance: "",
      accountType: "",
      status: "Active",
    },
    validationSchema: Yup.object({
      accountName: Yup.string().required("Account name is required"),
      holderName: Yup.string().required("Holder name is required"),
      accountNo: Yup.string().required("Account number is required"),
      openingBalance: Yup.string().required("Opening balance is required"),
      accountType: Yup.string().required("Account type is required"),
    }),
    onSubmit: async (values) => {
      try {
        let response;
        if (id) {
          response = await request.put("api/updateBankAccount/" + id, values);
        } else {
          response = await request.post("api/createAccount", values);
        }

        if (response?.data?.status) {
          toast.success("Account saved successfully!");
          navigate("/admin/bank-accounts");
        }
      } catch (error) {
        toast.error(
          error?.response?.data?.message ||
            "An error occurred. Please try again.",
        );
      }
    },
  });

  useEffect(() => {
    if (id) {
      const fetchAccount = async () => {
        setLoading(true);
        try {
          const response = await request.get(`api/getAccountById/${id}`);
          const account = response.data;
          formik.setValues({
            accountName: account.accountName || "",
            holderName: account.holderName || "",
            accountNo: account.accountNo || "",
            openingBalance: account.openingBalance?.toString() || "",
            accountType: account.accountType || "",
            status: account.status || "Active",
          });
          setLoading(false);
        } catch (error) {
          console.log(error);
          setLoading(false);
        }
      };

      fetchAccount();
    }
  }, [id]);

  return (
    <PageWrapper loading={loading}>
      <Box className="form-container">
        <Box className="form-page">
          <Box className="form-header">
            <PageHeader
              title={
                id && id !== "new" ? "Edit Account" : "Create New Account"
              }
              secondaryButton={{
                label: "Cancel",
                onClick: () => navigate("/admin/bank-accounts"),
              }}
              primaryButton={{
                label:
                  id && id !== "new" ? "Update Account" : "Create Account",
                icon: <span className="material-symbols-outlined">save</span>,
                onClick: () => formik.handleSubmit(),
              }}
            />
          </Box>

          <Box className="form-card">
            <Box className="form-card-body">
              <Box className="form-grid form-grid--2">
                <Box className="form-field">
                  <TextField
                    name="accountName"
                    label="Account Name"
                    type="text"
                    formik={formik}
                    autoFocus
                  />
                </Box>
                <Box className="form-field">
                  <TextField
                    name="holderName"
                    label="Holder Name"
                    type="text"
                    formik={formik}
                  />
                </Box>
              </Box>
              <Box
                className="form-grid form-grid--2"
                style={{ marginTop: "16px" }}
              >
                <Box className="form-field">
                  <TextField
                    name="accountNo"
                    label="Account No"
                    type="text"
                    formik={formik}
                  />
                </Box>
                <Box className="form-field">
                  <TextField
                    name="openingBalance"
                    label="Opening Balance"
                    type="number"
                    formik={formik}
                  />
                </Box>
              </Box>
              <Box
                className="form-grid form-grid--2"
                style={{ marginTop: "16px" }}
              >
                <Box className="form-field">
                  <SelectField
                    name="accountType"
                    label="Account Type"
                    formik={formik}
                    options={[
                      { value: "Bank", label: "Bank" },
                      { value: "UPI", label: "UPI" },
                    ]}
                  />
                </Box>
                <Box className="form-field">
                  <SelectField
                    name="status"
                    label="Status"
                    formik={formik}
                    options={[
                      { value: "Active", label: "Active" },
                      { value: "Inactive", label: "Inactive" },
                    ]}
                  />
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </PageWrapper>
  );
}
